import { EquipmentDetailForm } from "@/features/equipment/components/form/EquipmentDetailForm";
import { ItemsDetailForm } from "@/features/item/components/form/ItemsDetailForm";
import { useModal } from "@/features/modal/ModalStackManager";
import { MoldStockCreateForm } from "@/features/moldStock/form/stockCreateForm";
import { ConsumeDetailForm } from "@/features/productionPerformance/components/consumeDetail/consumeDetailForm";
import { DefectDetailForm } from "@/features/productionPerformance/components/defectDetail/defectDetailForm";
import { HeaderControls } from "@/features/productionPerformance/components/headerControls";
import { ProductionPerformanceTable } from "@/features/productionPerformance/components/table";
import { ProductionPerformanceTotalTable } from "@/features/productionPerformance/components/totalTable";
import { useDeliveryDateInputs } from "@/features/productionPerformance/hooks/useDeliveryDateInputs";
import { useErpOutsourcingMutations } from "@/features/productionPerformance/hooks/useERPOutsourcingMutations";
import { ActionButtonBox } from "@/features/standard/ActionButtonBox";
import { ActionHeader } from "@/features/standard/ActionHeader";
import { ChipsBox } from "@/features/standard/Chips/ChipsBox";
import { Header } from "@/features/standard/Header";
import { Main } from "@/features/standard/Main";
import { HeaderSubTitle } from "@/features/standard/SubTitle";
import { HeaderTitle } from "@/features/standard/Title";
import { Wrapper } from "@/features/standard/Wrapper";
import { useStandardLayout } from "@/features/standardLayout/Context";
import { LinkAnchor } from "@/features/standardLayout/LinkAnchor";
import { ListTable } from "@/features/standardLayout/ListTable";
import { PaginationBox } from "@/features/standardLayout/PaginationBox";
import { StandardLayoutTableWrapper } from "@/features/standardLayout/Styles";
import { Calendar, isCalendarDate } from "@/features/ui/Calendar";
import { UsersDetailForm } from "@/features/users/components/form/UsersDetailForm";
import { usePub, useSub } from "@/hooks";
import { useLoader } from "@/hooks/useLoader";
import DefaultInstance from "@/instance/axios";
import { dataStyle, excelDownLoad, headerStyle } from "@/utils/excelDownLoad";
import { setToLocaleString } from "@/utils/unitMark";
import { Button, Chip, Flex, Select, Text, TextInput } from "@mantine/core";
import {
  SplitLotGet200Response,
  SplitLotGet200ResponseRowsInner,
  SplitLotGet200ResponseRowsInnerDefectsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import {
  IconCalendar,
  IconFileSpreadsheet,
  IconFolder,
  IconFolderCancel,
} from "@tabler/icons-react";
import { AxiosResponse } from "axios";
import dayjs from "dayjs";
import { debounce } from "es-toolkit";
import { useCallback, useEffect, useRef, useState } from "react";
import { Column, SelectColumn } from "react-data-grid";
import { useTranslation } from "react-i18next";

const ProductionPerformance = Object.assign({}, Main, {
  Header: Header,
  HeaderTitle: HeaderTitle,
  HeaderSubTitle: HeaderSubTitle,
  Wrapper: Wrapper,
  ActionHeader: ActionHeader,
  ActionButton: Button,
  ActionButtonBox: ActionButtonBox,
  ProductionPerformanceTable: ProductionPerformanceTable,
  MoldStockCreateForm: MoldStockCreateForm,
});

export const ProductionPerformanceOutsourcing2Page = () => {
  const publish = usePub();
  const { t } = useTranslation();
  const { setLoading, LoadingOverlay, loading } = useLoader();
  const {
    startDate,
    endDate,
    setPage,
    setPageSize,
    pageSize,
    page,
    setSelectedRows,
    data,
    refetch,
    setQuery,
    query,
    selectedRows,
  } = useStandardLayout<AxiosResponse<SplitLotGet200Response>>();
  const { openModal } = useModal();
  const [filterErpSync, setFilterErpSync] = useState(false);
  const searchInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});

  const focusedInputRef = useRef<string | null>(null);
  const inputAllRef = useRef<HTMLInputElement | null>(null);
  const [isTableInputFocused, setIsTableInputFocused] = useState(false);
  const localDate = localStorage.getItem("allDate");
  const [filterQueries, setFilterQueries] = useState<{ [key: string]: string }>({
    equipmentName: "",
    itemName: "",
    itemCode: "",
    itemSpec: "",
    erpWorkOrderNo: "",
    creatorUserCode: "",
    lotName: "",
  });
  // filterQueries가 바로 적용되지 않도록 debounce 처리
  const [debouncedFilter, setDebouncedFilter] = useState<{ [key: string]: string }>(
    () => filterQueries,
  );
  const {
    inputValues,
    setInputValues,
    selectedDates,
    inputRefs,
    handleInputChange,
    handleAllInputChange,
    handleDateChange,
    handleAllDateChange,
  } = useDeliveryDateInputs(localDate);

  const [inputDateText, setInputDateText] = useState<string>(
    localDate ?? inputValues[Array.from(selectedRows)[0]] ?? "",
  );
  // mutation Hooks
  const { postOutsourcingERP, cancelOutsourcingERP } = useErpOutsourcingMutations({
    setLoading,
    setSelectedRows,
    refetch,
  });

  // debounce 함수 정의
  const debouncedFilterChange = useCallback(
    debounce((newFilterQueries) => {
      setDebouncedFilter(newFilterQueries);
    }, 500), // 1000ms 지연 시간
    [],
  );

  // filterQueries가 변경될 때 debounce 함수 호출
  useEffect(() => {
    debouncedFilterChange(filterQueries);
  }, [filterQueries, debouncedFilterChange]);

  useEffect(() => {
    if (debouncedFilter) {
      // filterQueries가 변경되면 페이지를 1로 초기화
      setPage(1);
    }
  }, [debouncedFilter]);

  useEffect(() => {
    const initialInputValues: { [key: string]: string } = {};
    data?.data?.rows?.forEach((row) => {
      if (row.id) {
        initialInputValues[row.id] = row.workDate ?? localDate ?? "";
      }
    });
    setInputValues(initialInputValues);

    // localStorage에서 값이 변경되면 inputDateText도 업데이트
    if (localDate && localDate !== inputDateText) {
      setInputDateText(localDate);
    }
  }, [data?.data?.rows, localDate, inputDateText]);

  useEffect(() => {
    setQuery((prevQuery) => {
      // 중복 제거를 위한 키 목록
      const keysToReplace = [
        "createdAt",
        "routingOutsourceId",
        "checkErpSync",
        ...Object.keys(debouncedFilter),
      ];

      // 기존 조건 중 위 키가 포함되지 않은 조건만 유지
      const newConditions = prevQuery.filter((condition) => {
        const key = Object.keys(condition)[0];
        return !keysToReplace.includes(key);
      });

      // createdAt 조건 추가
      newConditions.push({
        createdAt: {
          $gte: dayjs(startDate).startOf("day").toISOString(),
          $lte: dayjs(endDate).endOf("day").toISOString(),
        },
      });

      // routingOutsourceId 조건 추가
      newConditions.push({
        routingOutsourceId: {
          $ne: null,
        },
      });

      // checkErpSync 조건은 filterErpSync 상태에 따라 추가
      if (filterErpSync) {
        newConditions.push({
          checkErpSync: {
            $or: [{ $eq: null }, { $eq: "D" }],
          },
        });
      }

      // filterQueries 조건 추가
      Object.entries(debouncedFilter).forEach(([key, value]) => {
        if (value) {
          newConditions.push({
            [key]: { $iLike: `%${value}%` },
          });
        }
      });

      return newConditions;
    });
  }, [startDate, endDate, filterErpSync, debouncedFilter]);

  const handleFilterChange = (e: any) => {
    const { name, value } = e.target;
    setFilterQueries((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
    searchInputRefs.current[name]?.focus();
  };

  const selectedWorkLogIds = Array.from(selectedRows)
    .map((row) => {
      try {
        return JSON.parse(row as string)?.id;
      } catch {
        return null;
      }
    })
    .filter((id): id is number => !!id);

  useEffect(() => {
    const changedField = Object.keys(filterQueries).find((key) => filterQueries[key] !== "");
    if (changedField && searchInputRefs.current[changedField]) {
      if (!isTableInputFocused) {
        searchInputRefs.current[changedField]?.focus();
      }
    }
  }, [filterQueries]);

  useEffect(() => {
    if (
      focusedInputRef.current &&
      inputRefs.current[focusedInputRef.current] &&
      isTableInputFocused
    ) {
      inputRefs.current[focusedInputRef.current]?.focus();
    }
  }, [inputValues, selectedDates, isTableInputFocused]);

  const handleFocus = (rowId: string) => {
    focusedInputRef.current = rowId;
    setIsTableInputFocused(true);
  };

  const columns: readonly Column<SplitLotGet200ResponseRowsInner | any>[] = [
    {
      ...SelectColumn,
      resizable: true,
    },
    {
      key: "checkErpSync",
      name: t("performance.checkErp"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {row.checkErpSync === "A"
              ? t("performance.syncComplete")
              : row.checkErpSync === "D"
                ? t("performance.syncCancel")
                : t("performance.syncWait")}
          </div>
        );
      },
    },
    {
      key: "createdAt",
      name: t("work.workDay"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {row.createdAt !== null ? dayjs(row.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
          </div>
        );
      },
    },
    {
      key: "erpSyncedAt",
      name: t("performance.erpSyncedAt"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {row.erpSyncedAt !== null ? dayjs(row.erpSyncedAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
          </div>
        );
      },
    },
    {
      key: "equipmentName",
      name: t("equipment.name"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <EquipmentDetailForm equipmentCode={row.equipmentCode} />,
                null,
                t("common.detail", { item: t("equipment.equipment") }),
              )
            }
          >
            {row.equipmentName}
          </LinkAnchor>
        );
      },
    },
    {
      key: "lotName",
      name: "LOT NO",
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() => openModal(<ConsumeDetailForm consumeData={row.consumeLogs} />, null, "")}
          >
            {row?.lotName}
          </LinkAnchor>
        );
      },
    },
    {
      key: "erpWorkOrderNo",
      name: t("workLog.workId"),
      sortable: true,
      resizable: true,
    },
    {
      key: "itemName",
      name: t("item.name"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() =>
              openModal(
                <ItemsDetailForm itemCode={row.itemCode} />,
                null,
                t("common.detail", { item: t("item.item") }),
              )
            }
          >
            {row.itemName}
          </LinkAnchor>
        );
      },
    },
    {
      key: "itemSpec",
      name: t("item.spec"),
      sortable: true,
      resizable: true,
    },
    {
      key: "productionSum",
      name: t("summary.todoQuantity"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex justify={"flex-end"} align="center">
            {row.productionSum}
          </Flex>
        );
      },
    },
    {
      key: "totalQuantity",
      name: t("workLog.totalQuantity"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <Flex justify={"flex-end"} align="center">
            {row.totalQuantity}
          </Flex>
        );
      },
    },
    {
      key: "defects",
      name: t("work.defectQuantity"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        const totalDefects =
          row.defects?.reduce(
            (sum: any, defect: SplitLotGet200ResponseRowsInnerDefectsInner) =>
              sum + defect.defect_sum,
            0,
          ) || 0;
        return (
          <LinkAnchor
            onClick={() => openModal(<DefectDetailForm defectData={row.defects} />, null, "")}
          >
            <span>{setToLocaleString(totalDefects)}</span>
          </LinkAnchor>
        );
      },
    },
    {
      key: "prevSplitLotCreatedAt",
      name: t("kpi.workStartTime"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {row.prevSplitLotCreatedAt !== null
              ? dayjs(row.prevSplitLotCreatedAt).format("YYYY-MM-DD HH:mm:ss")
              : "-"}
          </div>
        );
      },
    },
    {
      key: "createdAtEnd",
      name: t("kpi.workEndTime"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {row.createdAt !== null ? dayjs(row.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-"}
          </div>
        );
      },
    },
    {
      key: "timeDifference.hour",
      name: t("performance.workTime"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <div>
            {[
              row.timeDifference?.days ? `${row.timeDifference.days}${t("performance.day")}` : "",
              row.timeDifference?.hours
                ? `${row.timeDifference.hours}${t("performance.time")}`
                : "",
              row.timeDifference?.minutes
                ? `${row.timeDifference.minutes}${t("performance.minute")}`
                : "",
            ].join("")}
          </div>
        );
      },
    },
    {
      key: "creatorUserCode",
      name: t("workLog.worker"),
      sortable: true,
      resizable: true,
      formatter: ({ row }) => {
        return (
          <LinkAnchor
            onClick={() => openModal(<UsersDetailForm UserCode={row?.creatorUserCode} />, null, "")}
          >
            {row?.creatorUserCode}
          </LinkAnchor>
        );
      },
    },
    {
      key: "itemCode",
      name: t("item.code"),
      sortable: true,
      resizable: true,
    },
    {
      key: "workDate",
      name: t("performance.deliveryDay"),
      sortable: true,
      resizable: true,
      width: 160,
      formatter: ({ row }) => {
        return (
          <TextInput
            key={row.id}
            placeholder="YYYYMMDD"
            value={
              (inputValues[row.id] === undefined ? row.workDate : inputValues[row.id]) ?? localDate
            }
            onChange={(e) => handleInputChange(e, row.id)}
            ref={(el) => (inputRefs.current[row.id] = el)}
            onFocus={() => handleFocus(row.id)}
            onBlur={() => setIsTableInputFocused(false)}
            maxLength={8}
            autoFocus={false}
            rightSection={
              <IconCalendar
                onClick={() =>
                  openModal(
                    <Calendar
                      dates={selectedDates[row.id] || [new Date(), new Date()]}
                      type="default"
                    />,
                    null,
                    "",
                  ).then((result) => {
                    if (isCalendarDate(result)) {
                      handleDateChange(result, row.id); // 각 row.id에 맞는 날짜 업데이트
                    }
                  })
                }
              />
            }
          />
        );
      },
    },
  ];

  useSub(
    "excelDownLoad",
    async () => {
      try {
        setLoading(true);
        const response = await DefaultInstance.splitLotFindPost({
          query: JSON.stringify(query),
          sort: "-createdAt",
        });

        const data = response.data;

        const headerRow = [
          { v: "전송여부", t: "s", s: headerStyle },
          { v: "작업일", t: "s", s: headerStyle },
          { v: "전송일시", t: "s", s: headerStyle },
          { v: "설비명", t: "s", s: headerStyle },
          { v: "LOTNO", t: "s", s: headerStyle },
          { v: "작업지시번호", t: "s", s: headerStyle },
          { v: "품목명", t: "s", s: headerStyle },
          { v: "규격", t: "s", s: headerStyle },
          { v: "생산수량", t: "s", s: headerStyle },
          { v: "양품수량", t: "s", s: headerStyle },
          { v: "불량수량", t: "s", s: headerStyle },
          { v: "작업시작 시간", t: "s", s: headerStyle },
          { v: "작업종료 시간", t: "s", s: headerStyle },
          { v: "작업 시간", t: "s", s: headerStyle },
          { v: "작업자", t: "s", s: headerStyle },
          { v: "품목코드", t: "s", s: headerStyle },
          { v: "납품일", t: "s", s: headerStyle },
          { v: "자재소모내역", t: "s", s: headerStyle },
        ];
        const dataRows =
          data.map((item: SplitLotGet200ResponseRowsInner) => {
            const totalDefects =
              item.defects?.reduce(
                (sum: number, defect: SplitLotGet200ResponseRowsInnerDefectsInner) =>
                  sum + (defect.defect_sum ?? 0),
                0,
              ) ?? 0;

            return [
              {
                v:
                  item.checkErpSync === "A"
                    ? "전송완료"
                    : item.checkErpSync === "D"
                      ? "전송취소"
                      : "전송대기",
                t: "s",
                s: dataStyle,
              },
              {
                v: item.createdAt ? dayjs(item.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-",
                t: "s",
                s: dataStyle,
              },
              {
                v: item.erpSyncedAt ? dayjs(item.erpSyncedAt).format("YYYY-MM-DD HH:mm:ss") : "-",
                t: "s",
                s: dataStyle,
              },
              { v: item.equipmentName ?? "", t: "s", s: dataStyle },
              { v: item.lotName ?? "", t: "s", s: dataStyle },
              { v: item.erpWorkOrderNo?.toString() ?? "", t: "s", s: dataStyle },
              { v: item.itemName ?? "", t: "s", s: dataStyle },
              { v: item.itemSpec?.toString() ?? "", t: "s", s: dataStyle },
              { v: setToLocaleString(item.productionSum) ?? "", t: "s", s: dataStyle },
              { v: setToLocaleString(item.totalQuantity) ?? "", t: "s", s: dataStyle },
              { v: setToLocaleString(totalDefects), t: "s", s: dataStyle },
              {
                v: item.prevSplitLotCreatedAt
                  ? dayjs(item.prevSplitLotCreatedAt).format("YYYY-MM-DD HH:mm:ss")
                  : "-",
                t: "s",
                s: dataStyle,
              },
              {
                v: item.createdAt ? dayjs(item.createdAt).format("YYYY-MM-DD HH:mm:ss") : "-",
                t: "s",
                s: dataStyle,
              },
              {
                v: `${item.timeDifference?.days?.toString() ?? "-"}일 ${
                  item.timeDifference?.hours?.toString() ?? "-"
                }시간 ${item.timeDifference?.minutes?.toString() ?? "-"}분`,
                t: "s",
                s: dataStyle,
              },
              { v: item.creatorUserCode ?? "", t: "s", s: dataStyle },
              { v: item.itemCode ?? "", t: "s", s: dataStyle },
              { v: item.workDate ?? "", t: "s", s: dataStyle },
              {
                v:
                  item.consumeLogs
                    ?.map((consume) => {
                      const lotName = consume?.lotName ? `로트명 - ${consume.lotName}` : "";
                      const itemCode = consume?.itemCode ? `품목코드 - ${consume.itemCode}` : "";
                      const quantity = consume?.quantity ? `수량 : ${consume.quantity}` : "";

                      // 값이 있는 항목만 필터링하여 문자열로 결합
                      return [lotName, itemCode, quantity].filter(Boolean).join(" ");
                    })
                    .join(" ") ?? "",
                t: "s",
                s: dataStyle,
              },
            ];
          }) ?? [];

        await excelDownLoad({
          headerRow,
          dataRows,
          fileName: "외주실적조회",
        });
      } catch (e) {
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [JSON.stringify(query)],
  );

  return (
    <>
      <ProductionPerformance.Wrapper>
        <LoadingOverlay />
        <HeaderControls
          searchInputRefs={searchInputRefs}
          filterQueries={filterQueries}
          handleFilterChange={handleFilterChange}
        />
        {
          <Flex px="sm" direction="column" gap="xs">
            <Text fz="xl" fw={600}>
              {t("performance.outsourcePerformance")}
            </Text>
            <Text fz="xs">{t("performance.outsourcePerformanceSubTitle")}</Text>
          </Flex>
        }
        <Flex direction={"row"} gap="xs" align="center" justify="space-between" w="100%">
          <ChipsBox>
            <Chip
              variant="filled"
              size="sm"
              radius="xs"
              checked={filterErpSync || false}
              onClick={() => {
                setFilterErpSync((prev) => !prev);
                setSelectedRows(new Set());
                setPage(1);
              }}
            >
              {t("performance.erpInquiry")}
            </Chip>
          </ChipsBox>
          <Flex>
            <TextInput
              ref={inputAllRef}
              key={inputDateText}
              label={t("performance.deliveryDay")}
              styles={{
                label: {
                  display: "block",
                  textAlign: "right",
                  marginBottom: 0,
                  paddingRight: "0.5rem",
                },
                root: { display: "flex", flexDirection: "row", alignItems: "center" },
              }}
              placeholder="YYYYMMDD"
              defaultValue={localDate ?? ""}
              onChange={(e) => {
                const newValue = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 입력 가능
                setInputDateText(newValue);
                localStorage.setItem("allDate", newValue);
                setTimeout(() => {
                  if (inputAllRef.current) {
                    inputAllRef.current.focus(); // 🔥 달력 선택 후에도 포커스 유지
                  }
                }, 0);
                handleAllInputChange(e, Array.from(selectedRows));
              }}
              maxLength={8}
              autoFocus={false}
              rightSection={
                <IconCalendar
                  onClick={() =>
                    openModal(
                      <Calendar dates={[new Date(), new Date()]} type="default" />,
                      null,
                      "",
                    ).then((result) => {
                      if (isCalendarDate(result)) {
                        localStorage.setItem("allDate", dayjs(result[0]).format("YYYYMMDD"));
                        setInputDateText(dayjs(result[0]).format("YYYYMMDD"));

                        handleAllDateChange(result, Array.from(selectedRows)); // 각 row.id에 맞는 날짜 업데이트
                      }
                    })
                  }
                />
              }
            />
          </Flex>
          <Flex gap="1rem" pr="9rem">
            <Select
              data={[
                { value: "5", label: t("page.5") },
                { value: "10", label: t("page.10") },
                { value: "15", label: t("page.15") },
                { value: "20", label: t("page.20") },
              ]}
              value={pageSize?.toString() ?? "10"}
              onChange={(e) => {
                setPageSize(Number(e));
                setSelectedRows(new Set());
              }}
            />
            <Button
              leftIcon={<IconFolder />}
              onClick={() => {
                const createdAtMap = new Map<number, string>(
                  (data?.data?.rows ?? [])
                    .filter(
                      (row): row is { id: number; createdAt: string } =>
                        !!row.id && !!row.createdAt,
                    )
                    .map((row) => [row.id, dayjs(row.createdAt).format("YYYYMMDD")]),
                );

                // 최종 날짜
                const finalOSPDelvDates = selectedWorkLogIds.map(
                  (id) => inputValues[id] ?? inputDateText ?? createdAtMap.get(id) ?? null,
                );

                postOutsourcingERP({
                  erpSyncPushErpSplitOutsourcingRecordPostRequest: {
                    workLogIds: selectedWorkLogIds,
                    OSPDelvDates: finalOSPDelvDates,
                  },
                });
              }}
              disabled={
                data?.data?.rows?.some(
                  (row) => Array.from(selectedRows).includes(row.id) && row.checkErpSync === "A",
                ) || loading
              }
            >
              {t("performance.outsourcePerformanceSync")}
            </Button>
            <Button
              color="orange"
              leftIcon={<IconFolderCancel />}
              onClick={() =>
                cancelOutsourcingERP({
                  erpSyncPushErpSplitWorkRecordPostRequest: {
                    workLogIds: selectedWorkLogIds,
                  },
                })
              }
              disabled={
                data?.data?.rows?.some(
                  (row) => Array.from(selectedRows).includes(row.id) && row.checkErpSync !== "A",
                ) || loading
              }
            >
              {t("performance.outsourcePerformanceSyncCancel")}
            </Button>
            <Button
              color="teal"
              rightIcon={<IconFileSpreadsheet />}
              onClick={() => {
                publish("excelDownLoad");
              }}
            >
              {t("inventory.excelDownload")}
            </Button>
          </Flex>
        </Flex>
        <Flex w="100%" h="100%" p="sm" justify="space-between" gap="md" direction="column">
          <StandardLayoutTableWrapper>
            <ListTable<SplitLotGet200Response, SplitLotGet200ResponseRowsInner>
              columns={columns}
              rowKeyGetter={(row: SplitLotGet200ResponseRowsInner) => JSON.stringify(row)}
            />
          </StandardLayoutTableWrapper>
          <ProductionPerformanceTotalTable selectedRows={selectedRows} />
          <PaginationBox<SplitLotGet200Response> />
        </Flex>
      </ProductionPerformance.Wrapper>
    </>
  );
};
